import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";

import { type Au06Input, Au06InputSchema } from "@aucash/rules/au06";

import { useAuth } from "../../src/auth";
import { AppBar, Button, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space } from "../../src/theme";

// AU-06 input screen — HECS/HELP marginal repayment (spec §6.3). Repayment
// income is the single figure that drives the calc; most people only know their
// taxable income, so that's the primary field and the ATO add-backs (which lift
// repayment income above taxable income) sit behind an "advanced" toggle,
// defaulting to zero. Loan balance is optional — it only lets us express the
// repayment as a fraction of the balance, never a payoff date the user didn't
// ask for.

const ASSESSMENT_YEAR = "2025-26";

export default function Au06Index(): JSX.Element {
  const router = useRouter();
  const { status } = useAuth();

  const [taxableIncome, setTaxableIncome] = useState("");
  const [loanBalance, setLoanBalance] = useState("");
  const [showAddBacks, setShowAddBacks] = useState(false);
  const [fringeBenefits, setFringeBenefits] = useState("");
  const [superContributions, setSuperContributions] = useState("");
  const [investmentLoss, setInvestmentLoss] = useState("");
  const [foreignIncome, setForeignIncome] = useState("");

  const [errors, setErrors] = useState<{ income?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const input = useMemo<Au06Input | null>(() => {
    const raw = {
      assessmentYear: ASSESSMENT_YEAR,
      income: {
        taxableIncome: Number(taxableIncome),
        reportableFringeBenefits: Number(fringeBenefits || "0"),
        reportableSuperContributions: Number(superContributions || "0"),
        netInvestmentLoss: Number(investmentLoss || "0"),
        exemptForeignEmploymentIncome: Number(foreignIncome || "0"),
      },
      ...(loanBalance.trim() !== "" ? { loanBalance: Number(loanBalance) } : {}),
    };
    const parsed = Au06InputSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }, [
    taxableIncome,
    fringeBenefits,
    superContributions,
    investmentLoss,
    foreignIncome,
    loanBalance,
  ]);

  const onSubmit = (): void => {
    const next: { income?: string } = {};
    if (taxableIncome.trim() === "") {
      next.income = "Enter your taxable income for this year.";
    }
    setErrors(next);
    if (Object.keys(next).length > 0 || input === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    trpc.calc.au06
      .mutate({ input })
      .then(({ id }) => {
        router.push({ pathname: "/au06/result", params: { id } });
      })
      .catch((err: unknown) => {
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't save your result.",
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="HECS / HELP" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
            {status === "authed" ? (
              <Pressable onPress={() => router.push("/my-results")} accessibilityRole="button">
                <Text style={styles.navLink}>My results</Text>
              </Pressable>
            ) : status === "anon" ? (
              <Pressable onPress={() => router.push("/auth")} accessibilityRole="button">
                <Text style={styles.navLink}>Sign in</Text>
              </Pressable>
            ) : null}
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>FY26 · marginal repayment</Eyebrow>
            <Text style={styles.title}>What will your HECS repayment be?</Text>
            <Text style={styles.subtitle}>
              See your compulsory study and training loan repayment for FY26 under
              the new marginal system, calculated from current ATO thresholds.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="Your taxable income for FY26"
            help="Your assessable income minus deductions — the figure on your tax return. We add the items below to get your repayment income."
            error={errors.income}
          >
            <MoneyInput
              value={taxableIncome}
              onChange={setTaxableIncome}
              testID="input-taxable-income"
              accessibilityLabel="Your taxable income for FY26"
            />
          </Field>

          <Field
            label="Current HELP / HECS balance"
            help="Optional. Lets us show your repayment as a share of the balance. We don't model indexation or a payoff date."
          >
            <MoneyInput
              value={loanBalance}
              onChange={setLoanBalance}
              accessibilityLabel="Current HELP or HECS balance"
            />
          </Field>

          <Pressable
            onPress={() => setShowAddBacks((v) => !v)}
            accessibilityRole="button"
            accessibilityState={{ expanded: showAddBacks }}
            style={styles.advancedToggle}
          >
            <Text style={styles.advancedToggleText}>
              {showAddBacks ? "− " : "+ "}
              Add other income (advanced)
            </Text>
          </Pressable>

          {showAddBacks ? (
            <View style={styles.advancedPanel}>
              <Text style={styles.advancedHint}>
                Repayment income adds these back to your taxable income. Leave them
                blank if they don&apos;t apply. Source: ATO income tests.
              </Text>
              <Field label="Reportable fringe benefits">
                <MoneyInput value={fringeBenefits} onChange={setFringeBenefits} accessibilityLabel="Reportable fringe benefits" />
              </Field>
              <Field label="Reportable super contributions">
                <MoneyInput value={superContributions} onChange={setSuperContributions} accessibilityLabel="Reportable super contributions" />
              </Field>
              <Field label="Total net investment loss">
                <MoneyInput value={investmentLoss} onChange={setInvestmentLoss} accessibilityLabel="Total net investment loss" />
              </Field>
              <Field label="Exempt foreign employment income">
                <MoneyInput value={foreignIncome} onChange={setForeignIncome} accessibilityLabel="Exempt foreign employment income" />
              </Field>
            </View>
          ) : null}

          <Button
            label={submitting ? "Saving…" : "See your repayment →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="au06-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  advancedToggle: { paddingVertical: space.xs },
  advancedToggleText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  advancedPanel: {
    gap: space.lg,
    padding: space.lg,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    backgroundColor: colors.sand,
  },
  advancedHint: { fontFamily: fonts.body, fontSize: 13, lineHeight: 18, color: colors.inkSoft },
  submitError: { fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.danger, textAlign: "center" },
});
